import {
  NUMBERING_TOOL_STROKE_COLOR,
  SELECTION_TOOL_STROKE_COLOR
} from "@gettactic/map-builder/src/components/map/defines";
import { generateNextSpotLabels } from "@gettactic/map-builder/src/components/map/helpers/generateNextSpotLabels";
import { updateShape } from "../document/operations";
import { isPrimaryButton } from "../gesture/pointer";
import type { Point } from "../types";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";

/**
 * Freehand stroke over the map. `numbering` relabels the spots it crosses
 * in stroke order from the label pattern; `draw-select` selects every
 * shape it crosses. Hits are found from the DOM under the pointer.
 */
export const createStrokeTool = (kind: "numbering" | "draw-select"): Tool => {
  let points: Point[] = [];
  let hits: string[] = [];
  let context: ToolContext | null = null;
  let isDrawing = false;

  const frame = createFrameScheduler<Point>((client) => {
    if (!context || !isDrawing) return;
    const world = context.toWorld(client);
    points.push(world);
    const element = document.elementFromPoint(client.x, client.y);
    const id = context.hitShape(element);
    if (id && !hits.includes(id)) {
      const shape = context.store.getState().shapes[id];
      const isMatch =
        kind === "draw-select"
          ? !!shape && !shape.locked
          : shape?.type === "spot";
      if (isMatch) hits.push(id);
    }
    context.overlay.set({
      draft: {
        kind: "freehand",
        points: [...points],
        color:
          kind === "draw-select"
            ? SELECTION_TOOL_STROKE_COLOR
            : NUMBERING_TOOL_STROKE_COLOR
      }
    });
  });

  const reset = () => {
    frame.cancel();
    isDrawing = false;
    points = [];
    hits = [];
    context?.overlay.reset();
  };

  return {
    kind,
    cursor: "crosshair",
    isBusy: () => isDrawing,
    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      isDrawing = true;
      points = [toolContext.toWorld({ x: event.clientX, y: event.clientY })];
      hits = [];
    },
    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!isDrawing) return;
      frame.schedule({ x: event.clientX, y: event.clientY });
    },
    onPointerUp(toolContext) {
      context = toolContext;
      if (!isDrawing) return;
      const crossed = hits;
      reset();
      const state = toolContext.store.getState();
      if (kind === "draw-select") {
        state.setSelection(crossed);
        return;
      }
      let label = state.meta.lastSpotLabel ?? [];
      const labels = new Map<string, string>();
      for (const id of crossed) {
        const next = generateNextSpotLabels(state.meta.spotLabelPattern, label);
        if (!next) break;
        label = next;
        labels.set(id, next.join(""));
      }
      if (labels.size === 0) return;
      state.commit((document) =>
        [...labels.entries()].reduce(
          (current, [id, text]) => updateShape(current, id, { label: text }),
          document
        )
      );
      state.setMeta({ lastSpotLabel: label });
    },
    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && isDrawing) {
        context = toolContext;
        reset();
        return true;
      }
      return false;
    },
    cancel(toolContext) {
      context = toolContext;
      reset();
    }
  };
};
